import React, { FC } from 'react';
import { Button, Chip, Grid, makeStyles, MenuItem, TextField, Theme } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { isValid } from 'date-fns';

import { APP_LOG_ACTION_LABELS, APP_LOG_ENTITY_LABELS, APP_LOG_SOURCE_LABELS } from 'constants/appLog';

export interface AppLogFilters {
  source: string;
  action: string;
  entityType: string;
  startDate: Date | null;
  endDate: Date | null;
  user?: { id: number; name: string };
  entity?: { type: string; id: number; label: string };
  requestId?: string;
}

export const emptyAppLogFilters: AppLogFilters = {
  source: '',
  action: '',
  entityType: '',
  startDate: null,
  endDate: null
};

interface Props {
  filters: AppLogFilters;
  onChange: (filters: AppLogFilters) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    minWidth: 150
  },
  datePicker: {
    width: 170
  },
  chips: {
    marginTop: theme.spacing(1),
    '& > *': {
      marginRight: theme.spacing(1)
    }
  }
}));

const AppLogFilter: FC<Props> = props => {
  const classes = useStyles();
  const { filters, onChange } = props;
  const { source, action, entityType, startDate, endDate, user, entity, requestId } = filters;

  const hasFilter = !!(source || action || entityType || startDate || endDate || user || entity || requestId);

  const handleChange = (changes: Partial<AppLogFilters>) => onChange({ ...filters, ...changes });

  const handleChangeDate = (field: 'startDate' | 'endDate', date: Date | null) => {
    // ignore what is typed until it is a full date
    if (date === null || isValid(date)) {
      handleChange({ [field]: date });
    }
  };

  const renderSelect = (label: string, value: string, labels: Record<string, string>, field: 'source' | 'action' | 'entityType') => (
    <TextField
      select
      size='small'
      variant='outlined'
      label={label}
      value={value}
      className={classes.select}
      onChange={event => handleChange({ [field]: event.target.value })}
    >
      <MenuItem value=''>All</MenuItem>
      {Object.keys(labels).map(key => (
        <MenuItem key={key} value={key}>
          {labels[key]}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <div>
      <Grid container spacing={1} alignItems='center'>
        <Grid item>{renderSelect('Source', source, APP_LOG_SOURCE_LABELS, 'source')}</Grid>
        <Grid item>{renderSelect('Action', action, APP_LOG_ACTION_LABELS, 'action')}</Grid>
        <Grid item>{renderSelect('Module', entityType, APP_LOG_ENTITY_LABELS, 'entityType')}</Grid>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Grid item>
            <KeyboardDatePicker
              clearable
              size='small'
              label='From'
              value={startDate}
              maxDate={endDate || undefined}
              variant='dialog'
              inputVariant='outlined'
              format='dd-MM-yyyy'
              placeholder='dd-MM-yyyy'
              className={classes.datePicker}
              onChange={date => handleChangeDate('startDate', date)}
            />
          </Grid>
          <Grid item>
            <KeyboardDatePicker
              clearable
              size='small'
              label='To'
              value={endDate}
              minDate={startDate || undefined}
              variant='dialog'
              inputVariant='outlined'
              format='dd-MM-yyyy'
              placeholder='dd-MM-yyyy'
              className={classes.datePicker}
              onChange={date => handleChangeDate('endDate', date)}
            />
          </Grid>
        </MuiPickersUtilsProvider>
        {hasFilter && (
          <Grid item>
            <Button color='primary' onClick={() => onChange(emptyAppLogFilters)}>
              Clear Filters
            </Button>
          </Grid>
        )}
      </Grid>
      {(user || entity || requestId) && (
        <div className={classes.chips}>
          {user && <Chip size='small' label={`User: ${user.name}`} onDelete={() => handleChange({ user: undefined })} />}
          {entity && (
            <Chip
              size='small'
              label={`${APP_LOG_ENTITY_LABELS[entity.type] || entity.type}: ${entity.label}`}
              onDelete={() => handleChange({ entity: undefined })}
            />
          )}
          {requestId && <Chip size='small' label='Same request' onDelete={() => handleChange({ requestId: undefined })} />}
        </div>
      )}
    </div>
  );
};

export default AppLogFilter;
